import type { ReactNode } from "react";
import card from "../tools/ToolCard.module.css";
import styles from "./OrganisationSpendSummary.module.css";

/** The icon at the head of each section on the Organisation page, by what the section breaks the spend down by. */
export type OrganisationSectionIcon = "tool" | "model" | "division" | "department" | "group" | "person" | "budget";

/** Each icon's path data, drawn in the shared 24-unit stroke style of the portal's other card icons. */
const iconPaths: Record<OrganisationSectionIcon, ReactNode> = {
  tool: <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-.6-.6-2.4z" />,
  model: (
    <>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4M10 10h4v4h-4z" />
    </>
  ),
  division: <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1M14 9h1M9 13h1M14 13h1M10 21v-4h4v4" />,
  department: <path d="m12 3 9 5-9 5-9-5 9-5ZM3 13l9 5 9-5M3 17l9 5 9-5" />,
  group: <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />,
  person: <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8" />,
  budget: (
    <>
      <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h14a2 2 0 0 1 2 2v3" />
      <path d="M3 5v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3" />
      <path d="M16 14h.01" />
    </>
  ),
};

/**
 * One section of the Organisation page as a card, in the style of the cards on My Tools and My Groups: the brand stripe,
 * an icon, the title and a line saying what the section shows, an optional action at the right of the header (a chart's
 * pie or over-time switch), and the section's chart and table beneath.
 *
 * @param props.icon - Which icon heads the card.
 * @param props.headingId - The title's id, which names the section for assistive technology.
 * @param props.sectionId - The section's own id, which a link can point at.
 * @param props.title - The section's title.
 * @param props.subtitle - One line saying what the section shows.
 * @param props.action - A control for the section, at the right of its header.
 * @param props.children - The section's chart and table.
 */
export function OrganisationSection({
  icon,
  headingId,
  sectionId,
  title,
  subtitle,
  action,
  children,
}: {
  icon: OrganisationSectionIcon;
  headingId: string;
  sectionId?: string;
  title: string;
  subtitle: string;
  action?: ReactNode;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <section id={sectionId} className={`${card.card} ${card.surface} ${styles.sectionCard}`} aria-labelledby={headingId} data-spotlight>
      <div className={card.header}>
        <span className={card.headerIcon} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {iconPaths[icon]}
          </svg>
        </span>
        <div className={card.headerText}>
          <h2 id={headingId} className={card.title}>
            {title}
          </h2>
          <p className={card.subtitle}>{subtitle}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
